import { useEffect, useRef } from 'react';
import { useMapContext, MapContextValue } from '@hero/web-context';

export interface UseMapPolylineOptions {
  isVisible: boolean | ((ctx: MapContextValue) => boolean);
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  showArrows?: boolean;
}

/**
 * Custom hook to manage Google Maps polylines directly
 * Avoids React Strict Mode duplication issues by managing polylines in useEffect
 * Uses MapContext internally to access map and locations
 *
 * @param options - Configuration for polylines
 * @returns MapContext value
 */
export function useMapPolyline(options: UseMapPolylineOptions) {
  const ctx = useMapContext();

  const {
    isVisible: isVisibleOption,
    strokeColor = '#3532A7',
    strokeWeight = 4,
    strokeOpacity = 0.8,
    showArrows = true,
  } = options;

  // Compute visibility from function or boolean
  const isVisible = typeof isVisibleOption === 'function' ? isVisibleOption(ctx) : isVisibleOption;

  // Track polyline instances - Map of index to polyline
  const polylinesRef = useRef<Map<number, google.maps.Polyline>>(new Map());

  useEffect(() => {
    // Clear all existing polylines
    const clearPolylines = () => {
      polylinesRef.current.forEach(polyline => {
        try {
          polyline.setMap(null);
        } catch (error) {
          // Ignore errors
        }
      });
      polylinesRef.current.clear();
    };

    // Don't render if not enough locations or not visible
    if (!ctx.map || ctx.locations.length < 2 || !isVisible) {
      clearPolylines();
      return;
    }

    // Clear old polylines before creating new ones
    clearPolylines();

    // Arrow configuration
    const arrowPath =
      typeof google !== 'undefined' && google.maps && google.maps.SymbolPath
        ? google.maps.SymbolPath.FORWARD_CLOSED_ARROW
        : 'M 0,-1 0,1';

    const arrowIcon = showArrows
      ? [
          {
            icon: {
              path: arrowPath,
              scale: 5.5,
              strokeColor: '#FFFFFF',
              strokeWeight: 2.5,
              fillColor: strokeColor,
              fillOpacity: 1,
            },
            offset: '15%',
            repeat: '200px',
          },
        ]
      : undefined;

    // Create polyline for each location pair
    ctx.locations.forEach((location, index) => {
      if (index === 0) return; // Skip first location (no previous location)

      const previousLocation = ctx.locations[index - 1];
      const path = [previousLocation.cords, location.cords];

      // Create polyline directly using Google Maps API
      const polyline = new google.maps.Polyline({
        path,
        map: ctx.map,
        strokeColor,
        strokeWeight,
        strokeOpacity,
        icons: arrowIcon,
      });

      polylinesRef.current.set(index, polyline);
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      clearPolylines();
    };
  }, [ctx.map, ctx.locations, isVisible, strokeColor, strokeWeight, strokeOpacity, showArrows]);

  return { ctx };
}
